import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import type { AppMode } from "../hooks/useTranslator";
import { Tooltip } from "../../../components/Tooltip";

interface HeaderModeToggleProps {
  appMode: AppMode;
  recording: boolean;
  modeTraductorLabel: string;
  modeMinutaLabel: string;
  modeAlert: string;
  setAppMode: (mode: AppMode) => void;
}

export const HeaderModeToggle: React.FC<HeaderModeToggleProps> = ({
  appMode,
  recording,
  modeTraductorLabel,
  modeMinutaLabel,
  modeAlert,
  setAppMode,
}) => {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      // 50% of the monitor/screen width
      const threshold = window.screen?.width ? window.screen.width * 0.5 : 600;
      setIsNarrow(window.innerWidth < threshold);
    };

    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const handleModeChange = (mode: AppMode) => {
    if (recording) {
      toast.error(modeAlert);
      return;
    }
    setAppMode(mode);
  };

  // Show compact mode indicator if window is narrow OR if recording
  if (isNarrow || recording) {
    const activeLabel = appMode === "traductor" ? modeTraductorLabel : modeMinutaLabel;
    const activeTooltip = appMode === "traductor" ? "Traducción activa + minuta" : "Minuta activa";

    return (
      <Tooltip text={`${activeTooltip} (Clic para cambiar)`}>
        <button
          onClick={() => handleModeChange(appMode === "traductor" ? "minuta" : "traductor")}
          className="font-sans text-xs font-[700] tracking-[0.05em] px-2 py-1 rounded-full border border-[var(--border)] bg-[var(--accent-muted)] text-[var(--accent)] whitespace-nowrap flex-shrink-0 select-none cursor-pointer hover:opacity-80 active:scale-95 transition-all"
        >
          {activeLabel}
        </button>
      </Tooltip>
    );
  }

  return (
    <div className="flex gap-0.5 bg-[var(--pill)] border border-[var(--border)] rounded-[9px] p-0.5 flex-shrink-0">
      <Tooltip text="Subtítulos en vivo + minuta al finalizar">
        <button
          onClick={() => handleModeChange("traductor")}
          aria-label={modeTraductorLabel}
          className={`font-sans text-xs font-[600] px-3 py-1 rounded-[7px] cursor-pointer transition-all duration-150 whitespace-nowrap relative ${
            appMode === "traductor"
              ? "bg-[var(--accent)] text-white shadow-[0_2px_8px_var(--accent-muted)]"
              : "text-[var(--text-muted)] bg-transparent hover:bg-[var(--surface-hover)] hover:text-[var(--text)]"
          }`}
        >
          {modeTraductorLabel}
        </button>
      </Tooltip>
      <Tooltip text="Minuta al finalizar">
        <button
          onClick={() => handleModeChange("minuta")}
          aria-label={modeMinutaLabel}
          className={`font-sans text-xs font-[600] px-3 py-1 rounded-[7px] cursor-pointer transition-all duration-150 whitespace-nowrap relative ${
            appMode === "minuta"
              ? "bg-[var(--accent)] text-white shadow-[0_2px_8px_var(--accent-muted)]"
              : "text-[var(--text-muted)] bg-transparent hover:bg-[var(--surface-hover)] hover:text-[var(--text)]"
          }`}
        >
          {modeMinutaLabel}
        </button>
      </Tooltip>
    </div>
  );
};
